import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X as Closed, Loader2 } from 'lucide-react';
import { cn, genJudgingSizeColor, judgingSizeToken } from '@/lib/utils';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { Button } from '@/components/ui/button';
import CustomTab from '@/widget/CustomTab';
import LightweightCharts from '../LightweightCharts';
import Activity from '../Activity';
import Holders from '../Holders';
import TokenData from '../TokenData';
import TokenInfo from './components/TokenInfo';
import { useTradeStore } from '../../store';
import { useAppStore } from '@/store';
import { KlineIcon, Lightning } from '@/widget/Icon';
import { formatBalanceWithCommas, formatPercentage } from '@/lib/format';
import BigNumber from 'bignumber.js';
import { SOL_TOKEN_ADDRESS } from '@/config';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import { useBscApprove } from '../../hook/useBscApprove';
import { useTheme } from '@/components/ThemeProvider';

interface IProfit {
  unrealizedPnl: string;
  unrealizedRoi: string;
}

export default () => {
  const { fwClass, chainInfo } = useAppStore();
  const { isConnected, transactions, chain, evmTransactions } = useBoomWallet();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslation();
  const { subscribe } = useAppEventBusContext();
  const { theme } = useTheme();
  const { tokenInfo, chainTokenAmount, bscTradeSetting, solTradeSetting } = useTradeStore();
  const targetChainTradingSetting = chain == 'bsc' ? bscTradeSetting : solTradeSetting;
  const { slippage, slippageAuto, fee, feeAuto } = targetChainTradingSetting;
  const { publish } = useAppEventBusContext();
  const { needApproval, approveLoading, bscApproveToken } = useBscApprove();
  const [profit, setProfit] = useState<IProfit>({ unrealizedPnl: '0', unrealizedRoi: '0' });

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Profit, setProfit);
    return () => unsubscribe();
  }, []);

  const [loading, setLoading] = useState(false);

  const handlerSolTradeSetting = async () => {
    try {
      if (!isConnected || !tokenInfo?.address) return;
      setLoading(true);
      const decimals = tokenInfo?.decimals;
      const [inputTokenAddress, outputTokenAddress] = [tokenInfo?.address, SOL_TOKEN_ADDRESS];
      await transactions.trade({
        inputTokenAddress,
        outputTokenAddress,
        tradeType: tokenInfo.dexType == 1 && tokenInfo.isCompleted ? 'jup' : tokenInfo.dexType == 1 && !tokenInfo.isCompleted ? 'pump' : 'jup',
        amountIn: BigNumber(chainTokenAmount)
          .times(10 ** decimals)
          .toFixed(0),
        ...(!feeAuto && { priorityFee: BigNumber(fee).times(1e9).toString() }),
        ...(!slippageAuto && { slippage: Math.round(Number(slippage) * 100) }),
      });
      setLoading(false);
    } catch (error) {}
  };

  const handlerBscTradeSetting = async () => {
    try {
      if (!tokenInfo?.address) return;
      setLoading(true);
      const { address, decimals, dexType, symbol, isCompleted } = tokenInfo;
      const tradeType = dexType === 1 ? (isCompleted ? 'pancake' : 'fourmeme') : 'pancake';
      const isPumpFromFourmeme = dexType == 1 ? true : false;
      const params = {
        inputToken: {
          inputTokenAddress: address,
          inputTokenDecimals: decimals,
          inputTokenName: symbol,
        },
        inputTokenAmount: BigNumber(chainTokenAmount).toFixed(10, BigNumber.ROUND_DOWN),
        isPumpFromFourmeme,
        tradeType,
        ...(!slippageAuto && { slippage: Number(slippage) * 100 }),
        ...(!feeAuto && { gasFee: (Number(fee) * 1e9).toString() }),
      };
      const hash = await evmTransactions?.sell?.(params as any);
      if (hash) {
        publish(SubscribeEvent.Update_Balance);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onSellAll = async () => {
    if (chain == 'solana') {
      handlerSolTradeSetting();
      return;
    } else {
      handlerBscTradeSetting();
    }
  };

  const SellBtn = useMemo(() => {
    if (chainInfo?.chain == 'solana') {
      return () => (
        <Button className={cn('w-full !bg-themeColor rounded-[12px] h-12 text-primaryTextColor', fwClass)} disabled={loading} onClick={onSellAll}>
          {loading ? <Loader2 className="animate-spin" /> : <Lightning />}
          {t('Trade.sellAll')}
        </Button>
      );
    }
    if (chainInfo.chain == 'bsc' && needApproval) {
      return () => (
        <Button
          className={cn('w-full !bg-[#FCD535] rounded-[12px] h-12 text-primaryTextColor', fwClass)}
          disabled={approveLoading}
          onClick={bscApproveToken}
        >
          {approveLoading ? <Loader2 className="animate-spin" /> : null}
          {t('Trade.authorize')}
        </Button>
      );
    }
    return () => (
      <Button className={cn('w-full !bg-themeColor rounded-[12px] h-12 text-primaryTextColor', fwClass)} disabled={loading} onClick={onSellAll}>
        {loading ? <Loader2 className="animate-spin" /> : <Lightning />}
        {t('Trade.sellAll')}
      </Button>
    );
  }, [approveLoading, chainInfo, needApproval, fwClass, tokenInfo, targetChainTradingSetting, loading]);

  const UnProfit = useMemo(() => {
    return () => (
      <div className="text-secondaryTextColor text-[12px] flex items-center justify-center mb-[8px]">
        <span>{t('Account.unrealizedProfit')}</span>
        <p className={cn('text-[14px]', genJudgingSizeColor(profit.unrealizedPnl))}>
          {judgingSizeToken(profit.unrealizedPnl) + formatBalanceWithCommas(profit.unrealizedPnl)} (
          {formatPercentage(profit.unrealizedRoi, { scale: true })})
        </p>
      </div>
    );
  }, [profit, fwClass]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger>
        <KlineIcon theme={theme} className="size-[18px]" />
      </SheetTrigger>
      <SheetContent
        side="bottom"
        hideCloseIcon
        className="w-full h-full overflow-y-auto p-0  border-t-[1px] border-t-borderColor flex flex-col justify-between outline-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-[16px] pt-0">
          <SheetHeader hidden />
          <SheetTitle hidden />
          <div className="flex justify-between sticky top-0 items-center h-[64px] z-10 bg-primaryBgColor">
            <div>
              <CustomTab
                tabs={[t('Trade.chart'), t('Trade.info')]}
                wrapperClassName={cn('text-[16px] gap-x-[16px]', fwClass)}
                activeIndex={activeIndex}
                onTabChange={setActiveIndex}
              />
            </div>
            <Closed className="size-[24px]" onClick={() => setOpen(false)} />
          </div>
          <div style={{ display: activeIndex == 0 ? 'block' : 'none' }}>
            <TokenData showTop10 />
            <LightweightCharts />
            {BigNumber(chainTokenAmount).gt(0) && (
              <div className="my-[60px]">
                <UnProfit />
                <SellBtn />
              </div>
            )}
            <div>
              <CustomTab
                tabs={[t('Trade.activity'), t('Trade.holders') + '(' + Number(tokenInfo?.holders).toLocaleString() + ')']}
                wrapperClassName={cn(fwClass, 'mb-[16px]')}
                activeIndex={activeTab}
                onTabChange={setActiveTab}
              />
              {activeTab == 0 ? <Activity /> : <Holders />}
            </div>
          </div>
          <div style={{ display: activeIndex == 1 ? 'block' : 'none' }}>
            <TokenInfo />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
